import { Link } from 'react-router-dom';
import { Sparkles, Mic, Swords, Camera, Users, ShoppingBag, Zap, Heart, TrendingUp, Star, Play, Crown, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MuziCoach from '../components/MuziCoach';
import { COACHES, TOP_STARS, EFFECT_PACKS, AVATARS_PRESET } from '../mock';

export default function Landing() {
  return (
    <div className="relative pt-16">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[85vh] flex items-center">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-7">
            <Badge className="bg-white/8 text-pink-300 border border-pink-500/30 px-3 py-1.5 rounded-full font-medium text-xs hover:bg-white/10">
              <Sparkles size={12} className="mr-1.5" /> Nouveau • Battle vs IA en temps réel
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight">
              Chante.<br />
              Joue. <br />
              <span className="text-gradient">Deviens viral.</span>
            </h1>
            <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-xl">
              Transforme ton visage en avatar IA, affronte tes amis dans des battles musicales,
              et fais exploser ta fanbase. Tout en un.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/studio">
                <Button size="lg" className="btn-neon h-14 px-7 text-base font-bold rounded-2xl text-white border-0 w-full sm:w-auto">
                  <Camera size={18} className="mr-2" /> Lancer le Studio
                </Button>
              </Link>
              <Link to="/battle">
                <Button size="lg" variant="outline" className="h-14 px-7 text-base font-bold rounded-2xl border-white/15 bg-white/5 hover:bg-white/10 text-white w-full sm:w-auto">
                  <Swords size={18} className="mr-2" /> Battle vs IA
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-6 pt-2">
              <div className="flex -space-x-2">
                {TOP_STARS.slice(0, 4).map((u) => (
                  <div key={u.id} className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 border-2 border-[#0A0A0F] flex items-center justify-center text-lg">
                    {u.avatar}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-white font-bold text-sm">+12 000 stars en herbe</div>
                <div className="flex items-center gap-1 text-yellow-400 text-xs">
                  {[1,2,3,4,5].map(i => <Star key={i} size={12} fill="currentColor" />)}
                  <span className="text-zinc-400 ml-1">4.9/5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Visual mock */}
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden glass border border-white/10 neon-pulse">
              <img
                src="https://images.unsplash.com/photo-1559011192-0e97c6558662?w=800"
                alt="MUZI Hero"
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-transparent to-transparent" />
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <div className="glass-light px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE
                </div>
                <div className="glass-light px-3 py-1.5 rounded-full text-xs font-bold">
                  👀 1.2K
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <MuziCoach coachId="c1" size="md" />
              </div>
            </div>
            <div className="absolute -top-6 -right-6 glass-light px-4 py-3 rounded-2xl flex items-center gap-2 float-soft hidden md:flex">
              <Crown className="text-yellow-400" size={20} />
              <div>
                <div className="text-xs text-zinc-400">Score</div>
                <div className="font-black text-white">98 500</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 glass-light px-4 py-3 rounded-2xl flex items-center gap-2 float-soft hidden md:flex">
              <Heart className="text-pink-500" size={20} fill="currentColor" />
              <div>
                <div className="text-xs text-zinc-400">Fans</div>
                <div className="font-black text-white">+248</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 PILIERS */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Badge className="bg-pink-500/15 text-pink-300 border-0 mb-4 px-3 py-1 rounded-full">L'expérience MUZI</Badge>
          <h2 className="text-4xl sm:text-5xl font-black mb-4">
            4 mondes,<br /><span className="text-gradient">1 seule app</span>
          </h2>
          <p className="text-zinc-400 text-lg">Tout ce qu'il faut pour exploser sur les réseaux.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Camera, title: 'Studio', desc: 'Avatar IA en temps réel, filtres néon, fonds personnalisés.', link: '/studio', color: 'from-pink-500 to-rose-600' },
            { icon: Swords, title: 'Battle', desc: 'Affronte l\'IA ou tes amis en ligne. Cartes vocales et silence.', link: '/battle', color: 'from-purple-500 to-indigo-600' },
            { icon: Mic, title: 'Karaoké Live', desc: 'Chante en live, ton coach IA réagit en direct.', link: '/live', color: 'from-blue-500 to-cyan-600' },
            { icon: Users, title: 'Fans', desc: 'Construis ta fanbase, monte au classement mondial.', link: '/fans', color: 'from-orange-500 to-pink-600' },
          ].map((p) => (
            <Link to={p.link} key={p.title}>
              <div className="glass rounded-3xl p-6 h-full card-hover group cursor-pointer">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <p.icon size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BATTLE MODES */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <Badge className="bg-purple-500/15 text-purple-300 border-0 mb-4 px-3 py-1 rounded-full">
              <Bot size={12} className="mr-1" /> Battle Mode
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Affronte l'IA<br />ou <span className="text-gradient">le monde entier</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-6">
              Deux modes, des dizaines de cartes, un seul gagnant. Vocal ou silence : choisis ton terrain.
            </p>
            <div className="space-y-3 mb-7">
              {[
                { icon: Bot, label: 'Mode vs IA', desc: 'Entraîne-toi 24/7 contre l\'IA à difficulté adaptative' },
                { icon: Users, label: 'Mode Online 1v1', desc: 'Défie un joueur aléatoire ou un ami' },
                { icon: TrendingUp, label: 'Mode Tournoi', desc: 'Battle royale jusqu\'à 8 joueurs' },
              ].map((f) => (
                <div key={f.label} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <f.icon size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-white">{f.label}</div>
                    <div className="text-sm text-zinc-400">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/battle">
              <Button className="btn-neon h-12 px-6 rounded-xl text-white border-0 font-bold">
                <Swords size={18} className="mr-2" /> Commencer une battle
              </Button>
            </Link>
          </div>

          <div className="relative">
            <div className="glass rounded-3xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                  <span className="text-xs font-bold text-pink-400">BATTLE EN COURS</span>
                </div>
                <div className="text-xs text-zinc-400">Round 3/5</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-4 rounded-2xl bg-white/5 border border-pink-500/30">
                  <div className="text-3xl mb-2">✨</div>
                  <div className="font-bold text-white text-sm">Toi</div>
                  <div className="text-2xl font-black text-pink-400">8</div>
                </div>
                <div className="text-center p-4 rounded-2xl bg-white/5 border border-purple-500/30">
                  <div className="text-3xl mb-2">🤖</div>
                  <div className="font-bold text-white text-sm">MUZI IA</div>
                  <div className="text-2xl font-black text-purple-400">5</div>
                </div>
              </div>
              <div className="glass-light rounded-2xl p-5 text-center">
                <div className="text-xs text-zinc-400 mb-1">CARTE EN COURS</div>
                <div className="text-5xl mb-2">🔥</div>
                <div className="text-2xl font-black text-white">FEU</div>
                <div className="text-xs text-zinc-400 mt-1">Chante avec INTENSITÉ</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COACHES */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge className="bg-blue-500/15 text-blue-300 border-0 mb-4 px-3 py-1 rounded-full">Coachs IA animés</Badge>
          <h2 className="text-4xl sm:text-5xl font-black mb-4">
            Choisis ton <span className="text-gradient">coach</span>
          </h2>
          <p className="text-zinc-400 text-lg">6 personnalités qui te boostent, te taquinent et te font progresser.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {COACHES.map((c) => (
            <div key={c.id} className="glass rounded-2xl p-5 text-center card-hover">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 flex items-center justify-center text-3xl mb-3 float-soft">
                {c.emoji}
              </div>
              <div className="font-bold text-white text-sm">{c.name}</div>
              <div className="text-xs text-zinc-400 mt-0.5">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BOUTIQUE TEASER */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <Badge className="bg-yellow-500/15 text-yellow-300 border-0 mb-3 px-3 py-1 rounded-full">
              <ShoppingBag size={12} className="mr-1" /> Boutique
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-black">Packs d'effets <span className="text-gradient">premium</span></h2>
          </div>
          <Link to="/shop" className="text-pink-400 hover:text-pink-300 font-semibold text-sm hidden sm:block">
            Voir tout →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {EFFECT_PACKS.map((p) => (
            <Link to="/shop" key={p.id}>
              <div className="glass rounded-2xl p-4 card-hover relative">
                {p.popular && (
                  <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full">POPULAIRE</div>
                )}
                <div className={`w-full aspect-square rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center text-5xl mb-3`}>
                  {p.emoji}
                </div>
                <div className="font-bold text-white text-sm truncate">{p.name}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-zinc-400">{p.count} effets</span>
                  <span className="text-pink-400 font-bold text-sm">{p.price}€</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="glass rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/15 via-purple-600/10 to-blue-500/15 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 glass-light px-3 py-1.5 rounded-full text-xs font-medium mb-5">
              <Zap size={12} className="text-yellow-400" /> 100% gratuit pour commencer
            </div>
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Prêt à devenir <span className="text-gradient">la prochaine star</span> ?
            </h2>
            <p className="text-zinc-300 text-lg mb-7 max-w-xl mx-auto">
              Rejoins +12 000 créateurs qui font le buzz tous les jours sur MUZI BATTLE.
            </p>
            <Link to="/studio">
              <Button size="lg" className="btn-neon h-14 px-8 text-base font-bold rounded-2xl text-white border-0">
                <Play size={18} className="mr-2" /> Lancer MUZI BATTLE
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
